import {
	ApolloClient,
	ApolloLink,
	ApolloProvider,
	HttpLink,
	InMemoryCache,
	Reference,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { datadogRum } from '@datadog/browser-rum';
import React, { ReactNode } from 'react';

import { ApiEndpoints } from '@utils/endpoints';
import { errorTypes } from '@utils/error';
import { isBrowser } from '@utils/isBrowser';

import { getAuthToken } from '@providers/AuthProvider';

// Apollo's dev-mode warnAboutDataLoss formatter crashes when it tries to
// String()-coerce null-prototype cache objects. Passing overwrite:true to
// every write skips that block entirely (guarded by !context.overwrite in
// writeToStore.js:55). Data semantics are unchanged.
export class SilentInMemoryCache extends InMemoryCache {
	write(options: Parameters<InMemoryCache['write']>[0]): Reference | undefined {
		return super.write({ ...options, overwrite: true });
	}
}

export const errorLink = onError(({ networkError = {} as any, graphQLErrors }) => {
	if (graphQLErrors) {
		graphQLErrors.map((error) => {
			console.log(
				`[GraphQL error]: Message: ${error.message}, Location: ${error.locations}, Path: ${error.path}`
			);
			datadogRum.addError(error, {
				errorType: errorTypes.apolloGraphql,
				locations: error.locations,
				path: error.path,
			});
		});
	}
	if (networkError) {
		console.log(`[Network err]: Message: ${networkError}`);
		switch (networkError.statusCode) {
			case 400:
			case 500:
				datadogRum.addError(networkError, { errorTypes: errorTypes.apolloNetwork });
				break;
			default:
		}
	}
});

export const authLink = setContext(async (_, { headers }) => {
	const authorizationToken = await getAuthToken();
	return {
		headers: {
			...headers,
			authorizationToken,
		},
	};
});

export const apolloClient = () => {
	const httpLink = new HttpLink({
		uri: (operation) =>
			`${ApiEndpoints.folxGraphql}?operation=${encodeURIComponent(operation.operationName)}`,
		...(!isBrowser && {
			fetch: () => {
				throw new Error("Can't make a dynamic query outside of a browser.");
			},
		}),
	});

	const link = ApolloLink.from([errorLink, authLink.concat(httpLink)]);

	const client = new ApolloClient({
		cache: new SilentInMemoryCache(),
		link,
	});

	return client;
};

const FolxApolloProvider = ({ children }: { children: ReactNode }) => {
	const client = apolloClient();
	return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default FolxApolloProvider;
